import { ChangeEvent } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

import { PageBody } from "../PageBody";
import { Label } from "../../atoms/Label";
import { Input } from "../../atoms/Input";
import { CheckInput } from "../../atoms/CheckInput";
import { Button } from "../../atoms/Button";
import { Typography } from "../../atoms/Typography";
import { ConfirmVerifyCodeModal } from "../../../containers/organisms/modal/ConfirmVerifyCodeModal";
import { openModal, closeModal } from "../../../graphql/local-mutations";
import { defaultTheme } from "../../themes";
import { Snackbar } from "../../atoms/Snackbar";
import okIcon from "../../../assets/icons/ok.png";
import errorIcon from "../../../assets/icons/alert.png";
import { SnackBarType } from "../../../types";

const ContentWrapper = styled.div`
  padding: 20px 40px;
  display: flex;
  flex-wrap: wrap;
`;

const ItemWrapper = styled.div<{ width?: string; marginBottom?: string }>`
  ${(props) => props.marginBottom && `margin-bottom: ${props.marginBottom}`};
  ${(props) => props.width && `width: ${props.width}`};
  padding: 0 30px 5px 0;
`;

const ButtonWrapper = styled.div<{ marginTop?: string; width?: string }>`
  ${(props) => props.marginTop && `margin-top: ${props.marginTop}`};
  width: ${(props) => props.width || "214px"};
  height: 44px;
`;

type Props = {
  verifyCode: string;
  permanent: boolean;
  confirmVerifyCodeModalOpened: boolean;
  isLoading: boolean;
  snackBar: SnackBarType;
  setVerifyCode: (verifyCode: string) => void;
  setPermanent: (permanent: boolean) => void;
  onCancelClick: () => void;
  onPublishVerifyCodeClick: () => void;
};

const modalName = "confirmVerifyCode";

/**
 * 確認番号の発行ページ
 */
export const PublishVerifyCodePage = ({
  verifyCode,
  permanent,
  confirmVerifyCodeModalOpened,
  snackBar,
  isLoading,
  setVerifyCode,
  setPermanent,
  onPublishVerifyCodeClick,
  onCancelClick,
}: Props) => {
  const onPagePublishClick = () => openModal(modalName);
  const onModalCancelClick = () => closeModal(modalName);
  const onVerifyCodeChange = (e: ChangeEvent<HTMLInputElement>) =>
    setVerifyCode(e.target.value);
  const onPermanentChange = (e: ChangeEvent<HTMLInputElement>) =>
    setPermanent(e.target.checked);

  return (
    <>
      <PageBody
        title={"確認番号の発行"}
        buttonTitle={"中止"}
        buttonType="danger"
        buttonOutline
        onButtonClick={onCancelClick}
        buttonIcon={
          <FontAwesomeIcon icon={faTimes} color={defaultTheme.colors.caution} />
        }
      >
        <ContentWrapper>
          <Label text={"確認番号"} margin="0 0 7px 0" />
          <ItemWrapper width="100%">
            <Input onChange={onVerifyCodeChange} value={verifyCode} />
          </ItemWrapper>
          <ItemWrapper width="100%">
            <CheckInput
              onChange={onPermanentChange}
              label="永続的に使用"
              checked={permanent}
              value="true"
            />
          </ItemWrapper>
          <ButtonWrapper width="334px">
            <Button
              onClick={onPagePublishClick}
              disabled={!verifyCode}
              buttonType={"primary"}
            >
              <Typography size={1}>{"発行して患者にメールを送る"}</Typography>
            </Button>
          </ButtonWrapper>
        </ContentWrapper>
      </PageBody>

      <ConfirmVerifyCodeModal
        isOpen={confirmVerifyCodeModalOpened}
        value={verifyCode}
        isLoading={isLoading}
        title={"確認番号を発行してもよろしいですか？"}
        onPublishClick={onPublishVerifyCodeClick}
        onCancelClick={onModalCancelClick}
      />

      {!!snackBar && !!verifyCode && (
        <Snackbar
          isOpen={snackBar.isOpen}
          icon={
            <img
              src={snackBar.type === "ok" ? okIcon : errorIcon}
              width="18"
              height="18"
              alt={snackBar.type}
            />
          }
          text={snackBar.message}
          close={snackBar.close}
        />
      )}
    </>
  );
};
